import { store } from "../store";
import { CONFIG } from "../config";
import { logger } from "../utils/logger";
import { parseJsonSafe } from "../utils/json";

/**
 * Service class for map operations and location management
 */
export class MapService {
  static getCurrent() {
    return store.getState().current;
  }

  static addCurrent(current) {
    //format dates to unix timestamp
    store.dispatch({
      type: "current/add",
      payload: current,
    });
  }

  static updateCurrent(current) {
    //format dates to unix timestamp
    store.dispatch({
      type: "current/update",
      payload: current,
    });
  }

  static addCurrentLocation(mapRef, deviceHistory) {
    try {
      // If we have device history, return the last location
      if (deviceHistory && deviceHistory.length > 0) {
        // Get last deviceHistory object data and parse it
        const data = parseJsonSafe(
          deviceHistory[deviceHistory.length - 1].data,
          null
        );
        if (!data) {
          return null;
        }

        // Return the coordinates and data for the marker
        if (data && data.longitude && data.latitude) {
          return {
            longitude: data.longitude,
            latitude: data.latitude,
            title: data.geocode || "Current Location",
            data: data,
          };
        }
      }

      // Return null if no valid location data is available
      return null;
    } catch (error) {
      logger.warn(error);
      return null;
    }
  }

  static updatePitch(pitch) {
    store.dispatch({
      type: "pitch/update",
      payload: pitch,
    });
  }

  static createGeoJSONCircleFeature(
    center,
    radiusInKm,
    points = 64,
    geocode = null,
    timeCreated = null,
    index = null,
    data = null
  ) {
    const coords = {
      longitude: center[0],
      latitude: center[1],
    };

    const km = radiusInKm;

    const ret = [];
    const distanceX =
      km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
    const distanceY = km / 110.54;

    let theta, x, y;
    for (let i = 0; i < points; i++) {
      theta = (i / points) * (2 * Math.PI);
      x = distanceX * Math.cos(theta);
      y = distanceY * Math.sin(theta);

      ret.push([coords.longitude + x, coords.latitude + y]);
    }
    ret.push(ret[0]);

    const feature = {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [ret],
      },
    };

    // Add properties if provided
    if (geocode || timeCreated || index !== null || data) {
      feature.properties = {
        ...(geocode && { geocode }),
        ...(timeCreated && { timeCreated }),
        ...(index !== null && { index }),
        ...(data && { data }),
        latitude: coords.latitude,
        longitude: coords.longitude,
      };
    }

    return feature;
  }

  static minMaxLngLat(deviceHistory) {
    if (!deviceHistory || deviceHistory.length === 0) {
      return null;
    }

    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;

    deviceHistory.forEach((item) => {
      let data = item.data;
      if (typeof data === "string") {
        data = parseJsonSafe(data, null);
        if (!data) {
          return;
        }
      }

      if (data.longitude && data.latitude) {
        const lng = parseFloat(data.longitude);
        const lat = parseFloat(data.latitude);

        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
    });

    if (minLng === Infinity) {
      return null;
    }

    return {
      minLng,
      maxLng,
      minLat,
      maxLat,
    };
  }

  // mapbox matching api call
  static async getMatch(coordinates, profile = "driving") {
    if (coordinates.length < 3) {
      return coordinates;
    }

    // Create the query
    const query = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coordinates}?access_token=${CONFIG.mapboxAccessToken}&geometries=geojson`,
      { method: "GET" }
    );
    const response = await query.json();

    // Handle errors
    if (response.code !== "Ok") {
      logger.warn("response error", response);
      return coordinates;
    }
    return response.routes[0].geometry.coordinates;
  }

  // use mapbox matching api to clean up the line
  static async getCleanedPolyline(coordinates, profile) {
    try {
      //remove any coordinates that are null or undefined or 0 or 0.9
      coordinates = coordinates.filter((coord) => {
        return (
          coord[0] !== null &&
          coord[1] !== null &&
          coord[0] !== undefined &&
          coord[1] !== undefined &&
          coord[0] !== 0 &&
          coord[1] !== 0 &&
          coord[0] !== 0.9 &&
          coord[1] !== 0.9
        );
      });
      // split coordinates into 25 point chunks, if last chunk is less than 100 points, add it to the previous chunk
      let chunks = [];
      let chunk = [];
      for (let i = 0; i < coordinates.length; i++) {
        if (chunk.length < 25) {
          chunk.push(coordinates[i]);
        } else {
          chunks.push(chunk);
          chunk = [];
          chunk.push(coordinates[i]);
        }
      }
      if (chunk.length > 0) {
        chunks.push(chunk);
      }

      // get the cleaned line for each chunk
      let cleanedLines = [];
      for (let i = 0; i < chunks.length; i++) {
        let cleanedLine = await this.getMatch(chunks[i].join(";"), profile);
        cleanedLines.push(cleanedLine);
      }

      // reduce the array of arrays to a single array
      cleanedLines = cleanedLines.reduce((acc, val) => acc.concat(val), []);

      return cleanedLines;
    } catch (e) {
      logger.warn("error cleaning line", e);
      return coordinates;
    }
  }

  //add markers for history
  static async addHistoryMarkers(mapRef, deviceHistory) {
    if (!mapRef) return;

    //if deviceHistory is empty, return
    if (!deviceHistory || deviceHistory.length < 1) return;

    //get features from history.data after json parsing
    let features = [];

    deviceHistory.forEach((item, index) => {
      let data = item.data;
      //json parse data
      if (typeof data === "string") {
        data = parseJsonSafe(data, null);
        if (!data) {
          return;
        }
      }

      //return coordinates
      features.push(
        this.createGeoJSONCircleFeature(
          [data.longitude, data.latitude],
          5,
          64,
          data.geocode,
          item.time_created,
          index,
          item.data
        )
      );
    });

    //create a geojson object from the features array
    features = {
      type: "FeatureCollection",
      features: features,
    };

    // if features.features.length === 1. empty the array
    if (features.features.length === 1) {
      features.features = [];
    }

    if (mapRef.loaded()) {
      if (!mapRef.getSource("device-history-markers")) {
        //add a source for device history markers
        mapRef.addSource("device-history-markers", {
          type: "geojson",
          data: features,
        });

        //add a layer for device history markers to use standard marker pin
        mapRef.addLayer({
          id: "device-history-markers-layer",
          type: "fill",
          source: "device-history-markers",
          paint: {
            "fill-color": "#4ecca3",
            "fill-opacity": 0.5,
            "fill-outline-color": "#004d40",
            //hide overflows
            "fill-antialias": false,
          },
          //hide overflows
          layout: {
            visibility: "visible",
          },

          filter: ["==", "$type", "Polygon"],
        });

        // Add click handler for markers
        mapRef.on("click", "device-history-markers-layer", (e) => {
          this.updateCurrent(e.features[0].properties.index);
          // Additional click handling logic can be added here
        });
      } else {
        //if source already exists, update the source data
        mapRef.getSource("device-history-markers").setData(features);
      }
    }
  }

  //add polyline for history
  static async addPolyline(mapRef, deviceHistory, deviceProfile = null) {
    try {
      //if device history.value is empty, return
      if (!deviceHistory || deviceHistory.length < 1) return;

      if (deviceProfile && deviceProfile.length) {
        //deviceProfile {"tracker":{"route_matching_type":"none","animated_lines":false}}
        //json parse deviceProfile
        if (typeof deviceProfile === "string") {
          deviceProfile = parseJsonSafe(deviceProfile, null);
          if (!deviceProfile) {
            return;
          }
        }
      }

      //create coords array for history.data items
      let coords = deviceHistory.map((item) => {
        let data = item.data;
        //json parse data
        if (typeof data === "string") {
          data = parseJsonSafe(data, null);
          if (!data) {
            return null;
          }
        }
        //return coordinates if not empty
        if (data.latitude && data.longitude) {
          if (data.latitude !== 0 && data.longitude !== 0) {
            return [data.longitude, data.latitude];
          } else {
            return null;
          }
        }
        return null;
      });

      //filter out null values
      coords = coords.filter((item) => item !== null);

      let polyLineGeometry = coords;

      //if route_matching_type is not none, get cleaned polyline
      if (
        deviceProfile &&
        deviceProfile.tracker &&
        deviceProfile.tracker.route_matching_type !== "none"
      ) {
        polyLineGeometry = await this.getCleanedPolyline(
          coords,
          deviceProfile.tracker.route_matching_type
        );
      }

      if (!mapRef.getSource("history")) {
        // add a source for the device's location history
        mapRef.addSource("history", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "LineString",
                  coordinates: polyLineGeometry,
                },
              },
            ],
          },
        });

        // Add line layer (simplified version - full animation logic can be added if needed)
        mapRef.addLayer({
          type: "line",
          source: "history",
          id: "line-background",
          paint: {
            "line-color": "#00D09F",
            "line-width": 3,
            "line-opacity": 1,
          },
        });
      } else {
        //if source already exists, update the source data
        mapRef.getSource("history").setData({
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: polyLineGeometry,
              },
            },
          ],
        });
      }
    } catch (err) {
      logger.warn(err);
    }
  }
}
